import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, updateDoc, doc, onSnapshot, addDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';

const ActivityContext = createContext();

export const useActivity = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
};

/** Visible catalogue public — filtre client (pas dans les rules Firestore). */
const isPubliclyVisible = (activity) =>
  activity?.status === 'approved' && activity?.isActive !== false;

export const ActivityProvider = ({ children }) => {
  const [activities, setActivities] = useState([]);
  const [allActivities, setAllActivities] = useState([]); // Toutes les activités pour le gestionnaire
  const [pendingActivities, setPendingActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);

  // Charger les activités approuvées depuis Firestore (pour le public)
  const loadActivities = useCallback(async () => {
    try {
      // status==approved seulement — isActive filtré ici (évite catalogue vide Firestore)
      const activitiesQuery = query(
        collection(db, 'activities'),
        where('status', '==', 'approved')
      );
      const activitiesSnapshot = await getDocs(activitiesQuery);
      const activitiesData = activitiesSnapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }))
        .filter(isPubliclyVisible);
      setActivities(activitiesData);
    } catch (error) {
      console.error('Erreur lors du chargement des activités:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les activités en attente (pour l'admin)
  const loadPendingActivities = useCallback(async () => {
    try {
      setLoadingPending(true);
      const pendingQuery = query(
        collection(db, 'activities'),
        where('status', '==', 'pending')
      );
      const pendingSnapshot = await getDocs(pendingQuery);
      const pendingData = pendingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPendingActivities(pendingData);
    } catch (error) {
      console.error('Erreur lors du chargement des activités en attente:', error);
      setPendingActivities([]);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  // Approuver une activité
  const approveActivity = useCallback(async (activityId) => {
    try {
      const activityRef = doc(db, 'activities', activityId);
      await updateDoc(activityRef, {
        status: 'approved',
        isActive: true,
        approvedAt: new Date()
      });
      
      // Retirer de la liste pending
      setPendingActivities(prev => prev.filter(a => a.id !== activityId));
      
      // Le listener onSnapshot mettra à jour automatiquement la liste des activités
      // Mais on force aussi un rechargement pour être sûr
      await loadActivities();
      
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'approbation de l\'activité:', error);
      return false;
    }
  }, [loadActivities]);

  // Rejeter une activité
  const rejectActivity = useCallback(async (activityId) => {
    try {
      const activityRef = doc(db, 'activities', activityId);
      await updateDoc(activityRef, {
        status: 'rejected',
        isActive: false,
        rejectedAt: new Date()
      });
      
      // Retirer de la liste pending
      setPendingActivities(prev => prev.filter(a => a.id !== activityId));
      
      return true;
    } catch (error) {
      console.error('Erreur lors du rejet de l\'activité:', error);
      return false;
    }
  }, []);

  // Charger les activités au montage et écouter les changements (catalogue public)
  useEffect(() => {
    loadActivities();

    const activitiesQuery = query(
      collection(db, 'activities'),
      where('status', '==', 'approved')
    );

    const unsubscribe = onSnapshot(
      activitiesQuery,
      (snapshot) => {
        const activitiesData = snapshot.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data()
          }))
          .filter(isPubliclyVisible);
        setActivities(activitiesData);
        setLoading(false);
      },
      (error) => {
        console.error('Erreur lors de l\'écoute des activités:', error);
        setLoading(false);
      }
    );

    // Pending : chargé à la demande (AdminPanel via loadPendingActivities), pas en global
    return () => unsubscribe();
  }, [loadActivities]);

  // Obtenir les activités actives (approuvées et actives)
  const getActiveActivities = useCallback(() => {
    const now = new Date();
    // Normaliser la date actuelle (sans heures) pour comparer uniquement les dates
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // Date limite pour considérer une activité comme récente (7 jours dans le passé)
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return activities.filter(activity => {
      // Vérifier le statut et l'état actif (les seules conditions obligatoires)
      if (activity.status !== 'approved') return false;
      if (!activity.isActive) return false;
      
      // Si l'activité n'a pas de date, l'afficher
      if (!activity.date) return true;
      
      // Si l'activité a une date, vérifier si elle est encore pertinente
      const activityDate = activity.date.toDate ? activity.date.toDate() : new Date(activity.date);
      const activityDateOnly = new Date(activityDate.getFullYear(), activityDate.getMonth(), activityDate.getDate());
      
      // Si l'activité a une date de fin, vérifier qu'elle n'est pas passée
      if (activity.endDate) {
        const endDate = activity.endDate.toDate ? activity.endDate.toDate() : new Date(activity.endDate);
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        // Afficher si la date de fin n'est pas passée (>= aujourd'hui)
        return endDateOnly >= today;
      } else {
        // Pas de date de fin : afficher si la date de début est dans les 7 derniers jours ou dans le futur
        // Cela permet d'afficher les activités récentes même si leur date est passée
        return activityDateOnly >= sevenDaysAgo;
      }
    }).sort((a, b) => {
      // Trier par date (les plus proches en premier)
      const dateA = a.date?.toDate ? a.date.toDate() : (a.date ? new Date(a.date) : new Date(0));
      const dateB = b.date?.toDate ? b.date.toDate() : (b.date ? new Date(b.date) : new Date(0));
      return dateA - dateB;
    });
  }, [activities]);

  // Charger toutes les activités (pour le gestionnaire)
  const loadAllActivities = useCallback(async () => {
    try {
      setLoadingAll(true);
      const activitiesSnapshot = await getDocs(collection(db, 'activities'));
      const activitiesData = activitiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllActivities(activitiesData);
    } catch (error) {
      console.error('Erreur lors du chargement de toutes les activités:', error);
      setAllActivities([]);
      toast.error('Erreur lors du chargement des activités');
    } finally {
      setLoadingAll(false);
    }
  }, []);

  // Créer une nouvelle activité
  const createActivity = useCallback(async (activityData) => {
    try {
      // Convertir la date en Timestamp si c'est une string
      let date = activityData.date;
      if (date && typeof date === 'string' && date.trim() !== '') {
        date = Timestamp.fromDate(new Date(date));
      } else {
        date = null;
      }

      // Convertir la date de fin en Timestamp si c'est une string
      let endDate = activityData.endDate;
      if (endDate && typeof endDate === 'string' && endDate.trim() !== '') {
        endDate = Timestamp.fromDate(new Date(endDate));
      } else {
        endDate = null;
      }

      // Extraire seulement l'URL string du media (Firestore ne peut pas stocker d'objets complexes imbriqués)
      const mediaUrl = activityData.media?.url || activityData.media?.data || (typeof activityData.media === 'string' ? activityData.media : null);
      
      const newActivity = {
        title: activityData.title,
        description: activityData.description,
        location: activityData.location,
        date: date,
        time: activityData.time || null,
        endDate: endDate,
        endTime: activityData.endTime || null,
        category: activityData.category || 'autre',
        organizer: activityData.organizer || null,
        contact: activityData.contact || null,
        email: activityData.email || null,
        media: mediaUrl,
        status: 'pending',
        isActive: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        views: 0
      };

      await addDoc(collection(db, 'activities'), newActivity);
      toast.success('Activité créée avec succès !');
      await loadAllActivities();
      return true;
    } catch (error) {
      console.error('Erreur lors de la création de l\'activité:', error);
      toast.error('Erreur lors de la création de l\'activité');
      return false;
    }
  }, [loadAllActivities]);

  // Modifier une activité
  const updateActivity = useCallback(async (activityId, activityData) => {
    try {
      const activityRef = doc(db, 'activities', activityId);
      
      // Convertir la date en Timestamp si c'est une string
      let date = activityData.date;
      if (date && typeof date === 'string' && date.trim() !== '') {
        date = Timestamp.fromDate(new Date(date));
      } else if (!date || date === '') {
        date = null;
      }
      // Si c'est déjà un Timestamp (date.toDate existe), on le garde tel quel

      // Convertir la date de fin en Timestamp si c'est une string
      let endDate = activityData.endDate;
      if (endDate && typeof endDate === 'string' && endDate.trim() !== '') {
        endDate = Timestamp.fromDate(new Date(endDate));
      } else if (!endDate || endDate === '') {
        endDate = null;
      }
      // Si c'est déjà un Timestamp (endDate.toDate existe), on le garde tel quel

      // Extraire seulement l'URL string du media (Firestore ne peut pas stocker d'objets complexes imbriqués)
      const mediaUrl = activityData.media?.url || activityData.media?.data || (typeof activityData.media === 'string' ? activityData.media : null);
      
      const updateData = {
        title: activityData.title,
        description: activityData.description,
        location: activityData.location,
        date: date,
        time: activityData.time || null,
        endDate: endDate,
        endTime: activityData.endTime || null,
        category: activityData.category || null,
        organizer: activityData.organizer || null,
        contact: activityData.contact || null,
        email: activityData.email || null,
        media: mediaUrl,
        updatedAt: Timestamp.now()
      };

      await updateDoc(activityRef, updateData);
      toast.success('Activité modifiée avec succès !');
      await loadAllActivities();
      // Recharger aussi les activités publiques si l'activité est approuvée
      const currentActivity = allActivities.find(a => a.id === activityId);
      if (currentActivity?.status === 'approved' || activityData.status === 'approved') {
        await loadActivities();
      }
      return true;
    } catch (error) {
      console.error('Erreur lors de la modification de l\'activité:', error);
      toast.error('Erreur lors de la modification de l\'activité');
      return false;
    }
  }, [loadAllActivities, loadActivities, allActivities]);

  // Supprimer une activité
  const deleteActivity = useCallback(async (activityId) => {
    try {
      const activityRef = doc(db, 'activities', activityId);
      await deleteDoc(activityRef);
      toast.success('Activité supprimée avec succès !');
      await loadAllActivities();
      setActivities(prev => prev.filter(a => a.id !== activityId));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'activité:', error);
      toast.error('Erreur lors de la suppression de l\'activité');
      return false;
    }
  }, [loadAllActivities]);

  // Activer/Désactiver une activité
  const toggleActivity = useCallback(async (activityId) => {
    try {
      const activityRef = doc(db, 'activities', activityId);
      const activity = allActivities.find(a => a.id === activityId);
      const currentlyActive = activity?.isActive !== false;
      const newIsActive = !currentlyActive;
      
      await updateDoc(activityRef, {
        isActive: newIsActive,
        updatedAt: Timestamp.now()
      });
      
      toast.success(newIsActive ? 'Activité activée !' : 'Activité désactivée !');
      await loadAllActivities();
      if (activity?.status === 'approved') {
        await loadActivities();
      }
      return true;
    } catch (error) {
      console.error('Erreur lors du changement d\'état de l\'activité:', error);
      toast.error('Erreur lors du changement d\'état de l\'activité');
      return false;
    }
  }, [allActivities, loadAllActivities, loadActivities]);

  // Ne pas charger automatiquement toutes les activités
  // Elles seront chargées manuellement via loadAllActivities() lorsque nécessaire (par exemple dans ActivityManager)

  const value = {
    activities,
    allActivities, // Pour le gestionnaire d'activités
    pendingActivities,
    getActiveActivities,
    loading,
    loadingAll,
    loadingPending,
    loadActivities,
    loadAllActivities,
    loadPendingActivities,
    createActivity,
    updateActivity,
    deleteActivity,
    toggleActivity,
    approveActivity,
    rejectActivity
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

